/**
 * dropsBatchCoverage
 *
 * Helpers for deciding when to warn the user that the fetched batch does not
 * reach the selected `dateFrom`. Used by Drops list UIs (page + tab).
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { getDropDateYmd } from '@trafficpulse/shared';

import type { SmswiserDrop } from '@/types/smswiser-drops';

function ymdToDmy(ymd: string): string {
  const [yyyy, mm, dd] = ymd.split('-');
  if (!yyyy || !mm || !dd) return ymd;
  return `${dd}/${mm}/${yyyy}`;
}

/** Returns the oldest YYYY-MM-DD found in the batch, or null when none parseable. */
export function getOldestDropYmd(drops: SmswiserDrop[]): string | null {
  let oldest: string | null = null;
  for (const drop of drops) {
    const ymd = getDropDateYmd({
      finishedAt: drop.finishedAt,
      executeAfter: drop.executeAfter,
      status: drop.status ? { startedAt: drop.status.startedAt ?? null } : null,
    });
    if (!ymd) continue;
    if (!oldest || ymd < oldest) oldest = ymd;
  }
  return oldest;
}

/**
 * Builds a user-facing message when the date filter is active and the batch
 * does not reach back to `dateFrom`. Returns null when no warning is needed.
 */
export function getDropsCoverageWarning(params: {
  drops: SmswiserDrop[];
  dateFrom: string;
  dateTo: string;
  isFetching: boolean;
}): string | null {
  if (params.isFetching) return null;
  if (!params.dateFrom || !params.dateTo) return null;
  if (params.drops.length === 0) return null;

  const oldest = getOldestDropYmd(params.drops);
  if (!oldest) return null;
  if (oldest <= params.dateFrom) return null;

  return `Loaded batch only reaches back to ${ymdToDmy(oldest)} — older drops in your range (${ymdToDmy(
    params.dateFrom,
  )} → ${ymdToDmy(params.dateTo)}) may still be missing. Try Fetch again or narrow the range.`;
}
